import { useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { Activity } from "lucide-react";
import { callApi } from "../api/api";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import Pagination from "../components/Pagination";

/** Supports flat, success-wrapped, and legacy array API shapes. */
function parseAuditLogsResponse(res) {
  const root = res?.data ?? {};

  // Flat paginated: { data: Row[], total, totalPages }
  if (Array.isArray(root.data)) {
    return {
      rows: root.data,
      total: Number(root.total) || 0,
    };
  }

  // success() wrapper: { success, data: { data, total } }
  const wrapped = root.data;
  if (
    wrapped &&
    typeof wrapped === "object" &&
    !Array.isArray(wrapped) &&
    Array.isArray(wrapped.data)
  ) {
    return {
      rows: wrapped.data,
      total: Number(wrapped.total) || 0,
    };
  }

  // Legacy success() wrapper: { success, data: Row[] }
  if (Array.isArray(wrapped)) {
    return { rows: wrapped, total: 0 };
  }

  return { rows: [], total: 0 };
}

export default function AuditLogs() {
  const { instance, accounts } = useMsal();

  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const pageSize = 10;
  const [expandedRow, setExpandedRow] = useState(null);

  const [filters, setFilters] = useState({
    email: "",
    resource: "",
  });

  const [loading, setLoading] = useState(false);
  const [isNarrow, setIsNarrow] = useState(false);


  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  /* ================= LOAD LOGS ================= */
  const loadLogs = useCallback(async () => {
    if (!accounts.length) return;

    try {
      setLoading(true);

      const query = `?page=${page}&limit=${pageSize}&email=${filters.email}&resource=${filters.resource}`;

      const res = await callApi(
        instance,
        accounts,
        "GET",
        `/api/audit/logs${query}`,
      );

      const { rows, total } = parseAuditLogsResponse(res);

      setLogs(rows);
      setTotalRecords(total);
    } catch (err) {
      console.error("Audit logs load failed", err);
      setLogs([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, page, pageSize, filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const hasFullPage = logs.length >= pageSize;
  const resolvedTotal =
    totalRecords > 0
      ? totalRecords
      : hasFullPage
        ? page * pageSize + pageSize
        : (page - 1) * pageSize + logs.length;
  const resolvedTotalPages =
    totalRecords > 0
      ? Math.max(1, Math.ceil(totalRecords / pageSize))
      : hasFullPage
        ? page + 1
        : Math.max(1, page);

  const paginationProps =
    logs.length > 0
      ? {
          page,
          totalPages: resolvedTotalPages,
          totalRecords: resolvedTotal,
          pageSize,
          currentPageCount: logs.length,
          onPageChange: setPage,
        }
      : null;

  /* ================= HELPERS ================= */

  const getStatusStyle = useCallback((status) => {
    if (status >= 200 && status < 300)
      return { bg: "#dcfce7", color: "#166534" };

    if (status >= 400 && status < 500)
      return { bg: "#fef3c7", color: "#92400e" };

    return { bg: "#fee2e2", color: "#991b1b" };
  }, []);

  const formatJson = useCallback((json) => {
    try {
      return JSON.stringify(JSON.parse(json), null, 2);
    } catch {
      return json || "—";
    }
  }, []);

  /* ================= RESPONSIVE CARD MAPPERS ================= */

  const mapLogCard = useCallback(
    (log) => {
      const statusStyle = getStatusStyle(log.response_status);
      const tone =
        log.response_status >= 200 && log.response_status < 300
          ? "success"
          : "info";
      return {
        id: `log-${log.log_id ?? log.created_at}`,
        icon: <Activity size={16} />,
        tone,
        title: log.user_email || "—",
        subtitle: log.api_name || "—",
        meta: [
          { label: "Resource", value: log.resource_code || "—" },
          { label: "Access", value: log.access_level || "—" },
          {
            label: "Date",
            value: log.created_at
              ? new Date(log.created_at).toLocaleString()
              : "—",
          },
        ],
        badges: [
          {
            value: `● ${log.response_status}`,
            tone:
              log.response_status >= 200 && log.response_status < 300
                ? "success"
                : undefined,
          },
        ],
        _statusStyle: statusStyle,
      };
    },
    [getStatusStyle],
  );

  const mapLogDrawer = useCallback(
    (log) => ({
      title: log.user_email || "—",
      fields: [
        { label: "API", value: log.api_name || "—" },
        { label: "Resource", value: log.resource_code || "—" },
        { label: "Access Level", value: log.access_level || "—" },
        { label: "Status Code", value: String(log.response_status ?? "—") },
        {
          label: "Date",
          value: log.created_at
            ? new Date(log.created_at).toLocaleString()
            : "—",
        },
        { label: "Request Payload", value: formatJson(log.request_payload) },
        { label: "Response Body", value: formatJson(log.response_body) },
      ],
    }),
    [formatJson],
  );

  const renderTable = () => (
    <>
      <div style={styles.card}>
        <ScrollableTable style={styles.tableWrap}>
          <table style={styles.table}>
            <colgroup>
              <col style={{ width: "18%" }} />
              <col style={{ width: "32%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "12%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "16%" }} />
            </colgroup>
            <thead>
              <tr style={styles.headRow}>
                <th style={{ ...styles.th, ...styles.thLeft }}>USER</th>
                <th style={{ ...styles.th, ...styles.thLeft }}>API</th>
                <th style={styles.th}>RESOURCE</th>
                <th style={styles.th}>ACCESS</th>
                <th style={styles.th}>STATUS</th>
                <th style={{ ...styles.th, ...styles.thRight }}>DATE</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" style={styles.empty}>
                    Loading logs...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" style={styles.empty}>
                    No audit logs found.
                  </td>
                </tr>
              ) : (
                logs.map((log, i) => {
                  const statusStyle = getStatusStyle(log.response_status);

                  return (
                    <>
                      <tr
                        key={i}
                        style={styles.row}
                        onClick={() =>
                          setExpandedRow(expandedRow === i ? null : i)
                        }
                      >
                        <td style={{ ...styles.td, ...styles.tdLeft }}>
                          <div style={styles.userCell}>{log.user_email}</div>
                        </td>

                        <td style={{ ...styles.td, ...styles.tdLeft }}>
                          <div style={styles.apiCell} title={log.api_name}>
                            {log.api_name}
                          </div>
                        </td>

                        <td style={styles.td}>
                          <span style={styles.codeBadge}>
                            {log.resource_code}
                          </span>
                        </td>

                        <td style={styles.td}>
                          <span style={styles.accessBadge}>
                            {log.access_level}
                          </span>
                        </td>

                        <td style={styles.td}>
                          <span
                            style={{
                              ...styles.statusBadge,
                              background: statusStyle.bg,
                              color: statusStyle.color,
                            }}
                          >
                            ● {log.response_status}
                          </span>
                        </td>

                        <td style={{ ...styles.tdMuted, ...styles.tdRight }}>
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                      </tr>

                      {/* EXPANDED */}
                      {expandedRow === i && (
                        <tr>
                          <td colSpan="6" style={styles.expanded}>
                            <div style={{ ...styles.expandGrid, ...(isNarrow ? styles.expandGridNarrow : null) }}>
                              <div style={styles.jsonCard}>
                                <div style={styles.jsonTitle}>
                                  Request Payload
                                </div>

                                <pre style={styles.pre} className="scrollable">
                                  {formatJson(log.request_payload)}
                                </pre>
                              </div>

                              <div style={styles.jsonCard}>
                                <div style={styles.jsonTitle}>
                                  Response Body
                                </div>

                                <pre style={styles.pre} className="scrollable">
                                  {formatJson(log.response_body)}
                                </pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              )}
            </tbody>
          </table>
        </ScrollableTable>
      </div>

      {paginationProps && <Pagination {...paginationProps} />}
    </>
  );

  /* ================= UI ================= */

  return (
    <div className="page-container" style={{ ...styles.container, ...(isNarrow ? styles.containerNarrow : null) }}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>Audit Logs</h2>

          <p style={styles.sub}>
            Track all API access, permissions, and system activity.
          </p>
        </div>
      </div>

      {/* FILTERS */}
      <div style={styles.filterCard}>
        <div style={{ ...styles.filterGrid, ...(isNarrow ? styles.filterGridNarrow : null) }}>
          <div style={{ ...styles.filterGroup, ...(isNarrow ? styles.filterGroupNarrow : null) }}>
            <label style={styles.label}>User Email</label>

            <input
              style={styles.input}
              placeholder="Search by user email..."
              value={filters.email}
              onChange={(e) =>
                setFilters((p) => ({
                  ...p,
                  email: e.target.value,
                }))
              }
            />
          </div>

          <div style={{ ...styles.filterGroup, ...(isNarrow ? styles.filterGroupNarrow : null) }}>
            <label style={styles.label}>Resource</label>

            <input
              style={styles.input}
              placeholder="Search by resource..."
              value={filters.resource}
              onChange={(e) =>
                setFilters((p) => ({
                  ...p,
                  resource: e.target.value,
                }))
              }
            />
          </div>

          <button
            style={{ ...styles.searchBtn, ...(isNarrow ? styles.searchBtnNarrow : null) }}
            onClick={loadLogs}
          >
            Search
          </button>
        </div>
      </div>

      {/* TABLE / CARDS */}
      <ResponsiveTableCards
        breakpoint={1024}
        data={logs}
        loading={loading}
        emptyTitle="No Audit Logs Found"
        emptySub="Try adjusting your filters or search terms."
        listTitle="Audit Logs"
        listSubtitle="Tap a log entry to view full details"
        cardMapper={mapLogCard}
        drawerMapper={mapLogDrawer}
        pagination={paginationProps || undefined}
        renderTable={renderTable}
      />
    </div>
  );
}

const styles = {
  container: {
    background: "#f8fafc",
    minHeight: "80vh",
  },

  containerNarrow: {
    minWidth: 0,
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "20px",
  },

  title: {
    margin: 0,
    fontSize: "28px",
    fontWeight: "700",
    color: "#0f172a",
  },

  sub: {
    color: "#64748b",
    marginTop: "6px",
    fontSize: "14px",
  },

  filterCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    padding: "18px",
    marginBottom: "20px",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },

  filterGrid: {
    display: "flex",
    gap: "16px",
    alignItems: "flex-end",
    flexWrap: "wrap",
  },

  filterGridNarrow: {
    flexDirection: "column",
    alignItems: "stretch",
  },

  filterGroup: {
    display: "flex",
    flexDirection: "column",
    flex: "1 1 0",
    minWidth: 0,
  },

  filterGroupNarrow: {
    width: "100%",
    flex: "1 1 auto",
  },

  label: {
    display: "block",
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "6px",
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: ".04em",
  },

  input: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    background: "#fff",
    fontSize: "14px",
    outline: "none",
    boxSizing: "border-box",
  },

  searchBtn: {
    height: "44px",
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "0 24px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: "600",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },

  searchBtnNarrow: {
    width: "100%",
  },

  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
  },

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1100px",
  },

  headRow: {
    borderBottom: "1px solid #e2e8f0",
    background: "#f8fafc",
  },

  th: {
    textAlign: "left",
    padding: "14px 18px",
    fontSize: "11px",
    fontWeight: "700",
    color: "#64748b",
    letterSpacing: ".06em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },

  row: {
    borderBottom: "1px solid #f1f5f9",
    cursor: "pointer",
    transition: "background 0.15s ease",
  },

  td: {
    padding: "16px 18px",
    fontSize: "14px",
    color: "#0f172a",
    verticalAlign: "middle",
  },

  tdMuted: {
    padding: "16px 18px",
    fontSize: "13px",
    color: "#64748b",
    whiteSpace: "nowrap",
  },

  userCell: {
    fontWeight: "600",
  },

  codeBadge: {
    display: "inline-flex",
    alignItems: "center",
    background: "#f1f5f9",
    color: "#0f172a",
    borderRadius: "999px",
    padding: "6px 10px",
    fontSize: "12px",
    fontFamily: "monospace",
    fontWeight: "600",
  },

  accessBadge: {
    display: "inline-flex",
    alignItems: "center",
    background: "#e0f2fe",
    color: "#075985",
    borderRadius: "999px",
    padding: "6px 10px",
    fontSize: "12px",
    fontWeight: "600",
  },

  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    borderRadius: "999px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "600",
  },

  expanded: {
    background: "#f8fafc",
    padding: "20px",
  },

  expandGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "18px",
  },

  jsonCard: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "12px",
    overflow: "hidden",
  },

  jsonTitle: {
    padding: "12px 14px",
    borderBottom: "1px solid #e2e8f0",
    fontWeight: "600",
    fontSize: "13px",
    background: "#f8fafc",
  },

  pre: {
    margin: 0,
    padding: "14px",
    maxHeight: "320px",
    overflow: "auto",
    fontSize: "12px",
    lineHeight: "1.5",
    fontFamily: "monospace",
    background: "#fff",
  },

  empty: {
    textAlign: "center",
    padding: "40px",
    color: "#64748b",
    fontSize: "14px",
  },

  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
  },

  pageBtn: {
    background: "#fff",
    border: "1px solid #dbe2ea",
    borderRadius: "10px",
    padding: "10px 16px",
    cursor: "pointer",
    fontWeight: "600",
  },

  pageText: {
    fontSize: "14px",
    color: "#475569",
    fontWeight: "600",
  },
};